/** @format */

import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * Keeps scroll position stable when new list items are appended (infinite scroll).
 */
export function usePreserveScrollOnAppend({ containerRef, itemCount, isLoadingMore }) {
  const prevCountRef = useRef(itemCount);
  const scrollHeightBeforeRef = useRef(0);

  useEffect(() => {
    if (isLoadingMore && containerRef.current) {
      scrollHeightBeforeRef.current = containerRef.current.scrollHeight;
    }
  }, [isLoadingMore, containerRef]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const grew = itemCount > prevCountRef.current;
    if (grew && scrollHeightBeforeRef.current > 0 && !isLoadingMore) {
      const delta = el.scrollHeight - scrollHeightBeforeRef.current;
      if (delta > 0) {
        el.scrollTop += delta;
      }
      scrollHeightBeforeRef.current = 0;
    }

    prevCountRef.current = itemCount;
  }, [itemCount, isLoadingMore, containerRef]);
}

/**
 * Restores a saved scrollTop only when the selected item changes — not on list growth.
 */
export function useRestoreScrollOnSelect({ containerRef, selectedId, scrollTopRef }) {
  const prevSelectedRef = useRef(selectedId);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (prevSelectedRef.current !== selectedId) {
      el.scrollTop = scrollTopRef.current ?? 0;
      prevSelectedRef.current = selectedId;
    }
  }, [selectedId, containerRef, scrollTopRef]);
}
